import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Drawer, Label } from '@feathr/components';

import { getAllColumns, getDefaultColumnIds, getEssentialColumnIds } from './TransactionColumns';

import * as styles from './TransactionColumnsDrawer.css';

interface IProps {
  columnIds: string[];
  isOpen: boolean;
  onClose: () => void;
  onApply: (columnIds: string[]) => void;
}

function TransactionColumnsDrawer({
  columnIds,
  isOpen,
  onClose,
  onApply,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const essentialColumnIds = getEssentialColumnIds();
  const allColumns = getAllColumns(t);
  const [localColumnIds, setLocalColumnIds] = useState<string[]>(columnIds);

  // Sync local state when parent columnIds change
  useEffect(() => {
    setLocalColumnIds(columnIds);
  }, [columnIds]);

  function handleToggleColumn(id: string): void {
    if (localColumnIds.includes(id)) {
      setLocalColumnIds(localColumnIds.filter((cid) => cid !== id));
    } else {
      setLocalColumnIds([...localColumnIds, id]);
    }
  }

  function handleSelectAll(): void {
    const allColumnIds = allColumns.map((col) => col.id);
    const allSelected = allColumnIds.every((id) => localColumnIds.includes(id));
    if (allSelected) {
      setLocalColumnIds([...essentialColumnIds]);
    } else {
      setLocalColumnIds([...allColumnIds]);
    }
  }

  function handleReset(): void {
    setLocalColumnIds(getDefaultColumnIds());
  }

  function handleApply(): void {
    onApply(localColumnIds);
    onClose();
  }

  function handleClose(): void {
    setLocalColumnIds(columnIds);
    onClose();
  }

  const allColumnIds = allColumns.map((col) => col.id);
  const allSelected = allColumnIds.every((id) => localColumnIds.includes(id));

  return (
    <Drawer
      isOpen={isOpen}
      name={'transaction-columns-drawer'}
      onClose={handleClose}
      title={t('Customize columns')}
    >
      <Drawer.Content addVerticalGap={true}>
        <Label>{t('Columns')}</Label>
        <div className={styles.columns}>
          {allColumns.map((column) => {
            const isEssential = essentialColumnIds.includes(column.id);
            return (
              <Checkbox
                disabled={isEssential}
                id={`checkbox-${column.id}`}
                key={column.id}
                label={column.checkboxLabel as string}
                name={`checkbox-${column.id}`}
                onChange={(): void => handleToggleColumn(column.id)}
                value={localColumnIds.includes(column.id)}
              />
            );
          })}
        </div>
      </Drawer.Content>
      <Drawer.Actions className={styles.actions}>
        <Button className={styles.resetButton} name={'reset-to-default'} onClick={handleReset}>
          {t('Reset to default')}
        </Button>
        <Button name={'select-all'} onClick={handleSelectAll}>
          {allSelected ? t('Unselect all') : t('Select all')}
        </Button>
        <Button name={'apply'} onClick={handleApply} type={'primary'}>
          {t('Apply')}
        </Button>
      </Drawer.Actions>
    </Drawer>
  );
}

export default TransactionColumnsDrawer;
